
import React, { useState, useEffect, useRef } from 'react';
import { Save, X, FileText, Eye, Edit2, Maximize2, Minimize2 } from 'lucide-react';

interface NoteEditorProps {
  problemId: string;
  problemTitle: string;
  initialContent?: string;
  onSave: (content: string) => Promise<void>;
  onClose: () => void;
  isLoading?: boolean;
}

const NoteEditor: React.FC<NoteEditorProps> = ({
  problemId,
  problemTitle,
  initialContent = '',
  onSave,
  onClose,
  isLoading = false,
}) => {
  const [content, setContent] = useState(initialContent);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);

  // Convert HTML to plain text
  const htmlToText = (html: string): string => {
    if (!html) return '';
    // Replace <br> tags with newlines
    let text = html.replace(/<br\s*\/?>/gi, '\n');
    // Remove other HTML tags
    const temp = document.createElement('div');
    temp.innerHTML = text;
    text = temp.textContent || temp.innerText || '';
    // Clean up multiple newlines
    return text.replace(/\n\n+/g, '\n\n');
  };

  useEffect(() => {
    // Convert HTML to plain text for textarea
    const plainText = htmlToText(initialContent);
    setContent(plainText);
  }, [initialContent]);

  const handleEditorInput = () => {
    const editor = editorRef.current;
    if (editor) {
      // Store HTML content
      const htmlContent = editor.innerHTML || '';
      setContent(htmlContent);
    }
  };

  const getTextNodes = (node: Node): Text[] => {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(
      node,
      NodeFilter.SHOW_TEXT,
      null
    );
    let textNode = walker.nextNode();
    while (textNode) {
      textNodes.push(textNode as Text);
      textNode = walker.nextNode();
    }
    return textNodes;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setCursorPosition = (editor: HTMLElement, position: number) => {
    // Wait for DOM to update, then set cursor
    requestAnimationFrame(() => {
      const textNodes = getTextNodes(editor);
      let offset = 0;
      const selection = window.getSelection();
      if (!selection) return;

      // Ensure editor is focused
      editor.focus();

      for (const node of textNodes) {
        const nodeLength = node.textContent?.length || 0;
        if (offset + nodeLength >= position) {
          const range = document.createRange();
          const targetOffset = Math.min(position - offset, nodeLength);
          range.setStart(node, targetOffset);
          range.setEnd(node, targetOffset);
          selection.removeAllRanges();
          selection.addRange(range);
          return;
        }
        offset += nodeLength;
      }

      // If position is beyond all text, place cursor at the end
      if (textNodes.length > 0) {
        const lastNode = textNodes[textNodes.length - 1];
        const range = document.createRange();
        range.setStart(lastNode, lastNode.textContent?.length || 0);
        range.setEnd(lastNode, lastNode.textContent?.length || 0);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getTextNodeAtPosition = (editor: HTMLElement, position: number): { node: Text; offset: number } | null => {
    const textNodes = getTextNodes(editor);
    let offset = 0;

    for (const node of textNodes) {
      const nodeLength = node.textContent?.length || 0;
      if (offset + nodeLength >= position) {
        return { node, offset: position - offset };
      }
      offset += nodeLength;
    }

    // If position is at the end, return the last text node
    if (textNodes.length > 0) {
      const lastNode = textNodes[textNodes.length - 1];
      return { node: lastNode, offset: lastNode.textContent?.length || 0 };
    }

    return null;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Save plain text content (textarea only supports plain text)
      // Convert newlines to <br> tags for HTML storage
      const htmlContent = content.split('\n').map(line => line || '<br>').join('<br>');
      await onSave(htmlContent);
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const applyFormatting = (command: string, value?: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();

    // Handle special cases
    if (command === 'code') {
      // Wrap selection in <code> tags
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const selectedText = range.toString();
        if (selectedText) {
          const codeElement = document.createElement('code');
          codeElement.textContent = selectedText;
          range.deleteContents();
          range.insertNode(codeElement);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          // Insert code element at cursor
          const codeElement = document.createElement('code');
          codeElement.textContent = 'code';
          range.insertNode(codeElement);
          // Select the inserted text
          range.setStartBefore(codeElement);
          range.setEndAfter(codeElement);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    } else if (command === 'codeBlock') {
      // Insert code block
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const preElement = document.createElement('pre');
        const codeElement = document.createElement('code');
        const selectedText = range.toString();
        codeElement.textContent = selectedText || 'code';
        preElement.appendChild(codeElement);
        range.deleteContents();
        range.insertNode(preElement);
        // Move cursor inside code block
        range.setStart(codeElement, 0);
        range.setEnd(codeElement, 0);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      // Use execCommand for standard formatting
      document.execCommand(command, false, value);
    }

    // Update content state from editor
    handleEditorInput();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const insertMarkdown = (prefix: string, suffix: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Ensure textarea is focused
    textarea.focus();

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    // Use textarea.value instead of content state to get current value
    const currentValue = textarea.value;
    const selectedText = currentValue.substring(start, end);
    const newText = currentValue.substring(0, start) + prefix + selectedText + suffix + currentValue.substring(end);

    setContent(newText);

    // Set cursor position after inserted text
    // Use requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      const updatedTextarea = textareaRef.current;
      if (updatedTextarea) {
        updatedTextarea.focus();
        const newStart = start + prefix.length;
        const newEnd = newStart + selectedText.length;
        // Position cursor at the end of inserted text (between prefix and suffix)
        updatedTextarea.setSelectionRange(newEnd, newEnd);
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderMarkdown = (text: string): string => {
    // Simple markdown to HTML conversion
    let html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-5 mb-3">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-6 mb-4">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-semibold">$1</strong>')
      .replace(/__(.*?)__/gim, '<strong class="font-semibold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
      .replace(/_(.*?)_/gim, '<em class="italic">$1</em>')
      // Code blocks
      .replace(/```([\s\S]*?)```/gim, '<pre class="bg-gray-800 text-green-400 p-4 rounded-lg my-4 overflow-x-auto"><code>$1</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/gim, '<code class="bg-gray-800 text-green-400 px-2 py-1 rounded">$1</code>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">$1</a>')
      // Lists
      .replace(/^\* (.*$)/gim, '<li class="ml-4">$1</li>')
      .replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>')
      // Line breaks
      .replace(/\n/gim, '<br />');

    // Wrap list items in ul tags
    html = html.replace(/(<li.*<\/li>)/gim, '<ul class="list-disc ml-6 my-2">$1</ul>');

    return html;
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      [contenteditable][data-placeholder]:empty:before {
        content: attr(data-placeholder);
        color: rgb(156, 163, 175);
        pointer-events: none;
      }
      [contenteditable][data-placeholder]:empty:focus:before {
        content: '';
      }
      .dark [contenteditable][data-placeholder]:empty:before {
        color: rgb(156, 163, 175);
      }
    `;
    document.head.appendChild(style);

    // Prevent body scroll when editor is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.head.removeChild(style);
      // Restore body scroll when editor is closed
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <div className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center ${isMaximized ? '' : 'p-4'}`}>
        <div className={`bg-white dark:bg-gray-800 ${isMaximized ? 'rounded-none' : 'rounded-lg'} shadow-2xl w-full ${isMaximized ? 'h-full' : 'max-w-4xl max-h-[90vh]'} flex flex-col transition-all duration-300`}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Notes: {problemTitle}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Problem ID: {problemId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsPreviewMode(!isPreviewMode)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={isPreviewMode ? 'Edit Mode' : 'Preview Mode'}
              >
                {isPreviewMode ? <Edit2 className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setIsMaximized(!isMaximized)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title={isMaximized ? 'Minimize' : 'Maximize'}
              >
                {isMaximized ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Toolbar */}
          {!isPreviewMode && (
            <div className="flex items-center space-x-2 p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              {/* <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                applyFormatting('bold');
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-100"
              title="Bold"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                applyFormatting('italic');
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-100"
              title="Italic"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                applyFormatting('code');
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-100"
              title="Inline Code"
            >
              <Code className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                applyFormatting('codeBlock');
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-100"
              title="Code Block"
            >
              <Code className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                applyFormatting('insertUnorderedList');
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-100"
              title="List"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                const url = prompt('Enter URL:');
                if (url) {
                  applyFormatting('createLink', url);
                }
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-700 dark:text-gray-100"
              title="Link"
            >
              <LinkIcon className="w-4 h-4" />
            </button> */}
              <div className="flex-1" />
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {content.split(/\s+/).filter(Boolean).length} words
              </div>
            </div>
          )}

          {/* Editor/Preview */}
          <div className="flex-1 overflow-hidden">
            {isPreviewMode ? (
              <div className="p-6 h-full overflow-y-auto bg-white dark:bg-gray-800">
                <div
                  className="max-w-none text-gray-900 dark:text-gray-100 whitespace-pre-wrap font-mono text-sm"
                  style={{
                    lineHeight: '1.6'
                  }}
                >
                  {content || <span style={{ color: 'rgb(156, 163, 175)', fontStyle: 'italic' }}>No notes yet. Start typing to add notes...</span>}
                </div>
              </div>
            ) : (
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onKeyDown={(e) => {
                  const textarea = textareaRef.current;
                  if (!textarea) return;

                  // Allow undo/redo to work (Cmd+Z / Ctrl+Z and Cmd+Shift+Z / Ctrl+Shift+Z)
                  if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
                    // Undo - allow default behavior
                    return;
                  }
                  if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z') && e.shiftKey) {
                    // Redo - allow default behavior
                    return;
                  }
                  if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
                    // Redo (alternative) - allow default behavior
                    return;
                  }

                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const value = textarea.value;
                  const beforeCursor = value.substring(0, start);
                  const afterCursor = value.substring(end);
                  const lineStart = beforeCursor.lastIndexOf('\n') + 1;
                  const currentLine = value.substring(lineStart, start);

                  // Handle Tab for indentation (like Sublime Text)
                  if (e.key === 'Tab') {
                    e.preventDefault();

                    // Check if it's a numbered list (1., 2., etc.), bullet list (-, *), or letter list (a., b., c.)
                    // Check if it's a numbered list (1., 2., etc.) - with or without space after dot
                    const isNumberedList = /^\s*\d+\.\s?/.test(currentLine);
                    const isBulletList = /^\s*[-*]\s/.test(currentLine);
                    const isLetterList = /^\s+[a-z]\.\s/i.test(currentLine);
                    const isRomanList = /^\s+[ivx]+\.\s/i.test(currentLine);

                    if (e.shiftKey) {
                      // Shift+Tab: Decrease indentation
                      if (currentLine.startsWith('  ')) {
                        const newValue = value.substring(0, lineStart) + currentLine.substring(2) + afterCursor;
                        setContent(newValue);
                        const newPos = start - 2;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      }
                    } else {
                      // Tab: Increase indentation
                      if (isNumberedList || isBulletList || isLetterList || isRomanList || currentLine.trim() === '') {
                        // Hierarchy: 1. → a. → i. → -
                        // Indent level determines format:
                        // 0 spaces: 1., 2., 3. (Arabic numbers)
                        // 2 spaces: a., b., c. (lowercase letters)
                        // 4 spaces: i., ii., iii. (Roman numerals)
                        // 6 spaces: -, * (bullets - text indented more than Roman numerals)

                        const indentLevel = (currentLine.match(/^(\s*)/)?.[1] || '').length;

                        // Check if it's a numbered list (1., 2., 3.) - with or without space after dot
                        const numberedMatch = currentLine.match(/^(\s*)(\d+)\.\s?(.*)$/);
                        if (numberedMatch) {
                          const rest = numberedMatch[3];

                          if (indentLevel === 0) {
                            // Level 0 → Level 1: Convert to lowercase letter, always start with 'a'
                            const newLine = '  a. ' + rest;
                            const newValue = value.substring(0, lineStart) + newLine + afterCursor;
                            setContent(newValue);
                            const newPos = lineStart + newLine.length;
                            setTimeout(() => {
                              textarea.setSelectionRange(newPos, newPos);
                            }, 0);
                          } else if (indentLevel === 2) {
                            // Level 1 → Level 2: Convert to Roman numeral, always start with 'i'
                            const newLine = '    i. ' + rest;
                            const newValue = value.substring(0, lineStart) + newLine + afterCursor;
                            setContent(newValue);
                            const newPos = lineStart + newLine.length;
                            setTimeout(() => {
                              textarea.setSelectionRange(newPos, newPos);
                            }, 0);
                          } else if (indentLevel === 4) {
                            // Level 2 → Level 3: Convert to bullet (6 spaces - text indented more than Roman numerals)
                            const newLine = '      - ' + rest;
                            const newValue = value.substring(0, lineStart) + newLine + afterCursor;
                            setContent(newValue);
                            const newPos = lineStart + newLine.length;
                            setTimeout(() => {
                              textarea.setSelectionRange(newPos, newPos);
                            }, 0);
                          } else {
                            // Just add indentation
                            const indent = '  ';
                            const newValue = value.substring(0, lineStart) + indent + currentLine + afterCursor;
                            setContent(newValue);
                            const newPos = start + indent.length;
                            setTimeout(() => {
                              textarea.setSelectionRange(newPos, newPos);
                            }, 0);
                          }
                        } else {
                          // Check if it's a lowercase letter (a., b., c.)
                          const letterMatch = currentLine.match(/^(\s+)([a-z])\.\s(.*)$/i);
                          if (letterMatch) {
                            // Level 1 → Level 2: Convert to Roman numeral, always start with 'i'
                            const rest = letterMatch[3];
                            const newLine = '    i. ' + rest;
                            const newValue = value.substring(0, lineStart) + newLine + afterCursor;
                            setContent(newValue);
                            const newPos = lineStart + newLine.length;
                            setTimeout(() => {
                              textarea.setSelectionRange(newPos, newPos);
                            }, 0);
                          } else {
                            // Check if it's a Roman numeral (i., ii., iii.)
                            const romanMatch = currentLine.match(/^(\s+)([ivx]+)\.\s(.*)$/i);
                            if (romanMatch && indentLevel === 4) {
                              // Level 2 → Level 3: Convert to bullet (6 spaces - text indented more than Roman numerals)
                              const rest = romanMatch[3];
                              const newLine = '      - ' + rest;
                              const newValue = value.substring(0, lineStart) + newLine + afterCursor;
                              setContent(newValue);
                              const newPos = lineStart + newLine.length;
                              setTimeout(() => {
                                textarea.setSelectionRange(newPos, newPos);
                              }, 0);
                            } else {
                              // Regular indentation
                              const indent = '  ';
                              const newValue = value.substring(0, lineStart) + indent + currentLine + afterCursor;
                              setContent(newValue);
                              const newPos = start + indent.length;
                              setTimeout(() => {
                                textarea.setSelectionRange(newPos, newPos);
                              }, 0);
                            }
                          }
                        }
                      } else {
                        // Regular tab insertion
                        const indent = '  ';
                        const newValue = value.substring(0, start) + indent + value.substring(end);
                        setContent(newValue);
                        const newPos = start + indent.length;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      }
                    }
                  }

                  // Handle Enter for numbered lists (auto-increment)
                  if (e.key === 'Enter') {
                    // Check for bullet points first (-, *)
                    const bulletMatch = currentLine.match(/^(\s*)([-*])\s(.*)$/);
                    if (bulletMatch) {
                      e.preventDefault();
                      const [, indent, bullet, rest] = bulletMatch;

                      // If line is empty, exit bullet list and continue parent list
                      if (!rest || rest.trim() === '') {
                        // Remove the current empty bullet line first
                        const beforeCurrentLine = value.substring(0, lineStart);
                        // Get content after cursor, removing the empty bullet line
                        let afterCurrentLine = afterCursor;

                        // Remove leading newline from afterCursor if it exists
                        if (afterCurrentLine.startsWith('\n')) {
                          afterCurrentLine = afterCurrentLine.substring(1);
                        }

                        // Find the parent list item by looking backwards
                        const lines = beforeCurrentLine.split('\n');
                        // parentItem would be used here in future implementation
                        const currentIndent = indent.length;

                        // Look backwards to find the last non-bullet list item with less indentation
                        for (let i = lines.length - 1; i >= 0; i--) {
                          const line = lines[i];
                          const lineIndent = (line.match(/^(\s*)/)?.[1] || '').length;

                          // Skip bullet lines when looking for parent
                          if (/^\s*[-*]\s/.test(line)) {
                            continue;
                          }

                          // Found a parent item (less indentation and not a bullet)
                          if (lineIndent < currentIndent) {
                            // Check if it's a Roman numeral (i., ii., iii.)
                            const romanMatch = line.match(/^(\s+)([ivx]+)\.\s(.*)$/i);
                            if (romanMatch) {
                              const [, parentIndent, roman, parentRest] = romanMatch;
                              if (parentRest && parentRest.trim() !== '') {
                                // Increment Roman numeral
                                const romanMap: { [key: string]: string } = {
                                  'i': 'ii', 'ii': 'iii', 'iii': 'iv', 'iv': 'v',
                                  'v': 'vi', 'vi': 'vii', 'vii': 'viii', 'viii': 'ix', 'ix': 'x'
                                };
                                const nextRoman = romanMap[roman.toLowerCase()] || 'ii';
                                // Ensure no double newline - beforeCurrentLine already ends with newline from previous line
                                const newLine = `${parentIndent}${nextRoman}. `;
                                const newValue = beforeCurrentLine + newLine + afterCurrentLine;
                                setContent(newValue);
                                const newPos = beforeCurrentLine.length + newLine.length;
                                setTimeout(() => {
                                  textarea.setSelectionRange(newPos, newPos);
                                }, 0);
                                return;
                              }
                            }

                            // Check if it's a lowercase letter (a., b., c.)
                            const letterMatch = line.match(/^(\s+)([a-z])\.\s(.*)$/i);
                            if (letterMatch) {
                              const [, parentIndent, letter, parentRest] = letterMatch;
                              if (parentRest && parentRest.trim() !== '') {
                                // Increment letter
                                const nextLetter = String.fromCharCode(letter.toLowerCase().charCodeAt(0) + 1);
                                // Ensure no double newline - beforeCurrentLine already ends with newline from previous line
                                const newLine = `${parentIndent}${nextLetter}. `;
                                const newValue = beforeCurrentLine + newLine + afterCurrentLine;
                                setContent(newValue);
                                const newPos = beforeCurrentLine.length + newLine.length;
                                setTimeout(() => {
                                  textarea.setSelectionRange(newPos, newPos);
                                }, 0);
                                return;
                              }
                            }

                            // Check if it's a numbered list (1., 2., 3.) - with or without space after dot
                            const numberedMatch = line.match(/^(\s*)(\d+)\.\s?(.*)$/);
                            if (numberedMatch) {
                              const [, parentIndent, number, parentRest] = numberedMatch;
                              if (parentRest && parentRest.trim() !== '') {
                                // Increment number
                                const nextNumber = parseInt(number) + 1;
                                // Ensure no double newline - beforeCurrentLine already ends with newline from previous line
                                const newLine = `${parentIndent}${nextNumber}. `;
                                const newValue = beforeCurrentLine + newLine + afterCurrentLine;
                                setContent(newValue);
                                const newPos = beforeCurrentLine.length + newLine.length;
                                setTimeout(() => {
                                  textarea.setSelectionRange(newPos, newPos);
                                }, 0);
                                return;
                              }
                            }
                          }
                        }

                        // No parent found, just exit the list (remove empty bullet line)
                        // beforeCurrentLine already ends with newline, so just append afterCurrentLine
                        const newValue = beforeCurrentLine + afterCurrentLine;
                        setContent(newValue);
                        const newPos = beforeCurrentLine.length;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      } else {
                        // Continue bullet list - insert another bullet (always use 6 spaces)
                        const bulletIndent = '      '; // 6 spaces for bullets
                        const newLine = `\n${bulletIndent}${bullet} `;
                        const newValue = value.substring(0, start) + newLine + afterCursor;
                        setContent(newValue);
                        const newPos = start + newLine.length;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      }
                      return;
                    }

                    // Check for sub-points (i., ii., iii., a., b., c.)
                    const subPointMatch = currentLine.match(/^(\s+)([a-z]+|i+v?|vi{0,3}|xi{0,3})\.\s(.*)$/i);
                    if (subPointMatch) {
                      e.preventDefault();
                      const [, indent, subPoint, rest] = subPointMatch;

                      // If line is empty, exit sub-point list and continue parent list
                      if (!rest || rest.trim() === '') {
                        // Remove the current empty sub-point line first
                        const beforeCurrentLine = value.substring(0, lineStart);
                        // Get content after cursor, removing the empty sub-point line
                        let afterCurrentLine = afterCursor;

                        // Remove leading newline from afterCursor if it exists
                        if (afterCurrentLine.startsWith('\n')) {
                          afterCurrentLine = afterCurrentLine.substring(1);
                        }

                        // Find the parent list item by looking backwards
                        const lines = beforeCurrentLine.split('\n');
                        const currentIndent = indent.length;

                        // Look backwards to find the last list item with less indentation
                        for (let i = lines.length - 1; i >= 0; i--) {
                          const line = lines[i];
                          const lineIndent = (line.match(/^(\s*)/)?.[1] || '').length;

                          // Found a parent item (less indentation)
                          if (lineIndent < currentIndent) {
                            // Check if it's a lowercase letter (a., b., c.)
                            const letterMatch = line.match(/^(\s+)([a-z])\.\s(.*)$/i);
                            if (letterMatch) {
                              const [, parentIndent, letter, parentRest] = letterMatch;
                              if (parentRest && parentRest.trim() !== '') {
                                // Increment letter
                                const nextLetter = String.fromCharCode(letter.toLowerCase().charCodeAt(0) + 1);
                                // Ensure no double newline - beforeCurrentLine already ends with newline from previous line
                                const newLine = `${parentIndent}${nextLetter}. `;
                                const newValue = beforeCurrentLine + newLine + afterCurrentLine;
                                setContent(newValue);
                                const newPos = beforeCurrentLine.length + newLine.length;
                                setTimeout(() => {
                                  textarea.setSelectionRange(newPos, newPos);
                                }, 0);
                                return;
                              }
                            }

                            // Check if it's a numbered list (1., 2., 3.) - with or without space after dot
                            const numberedMatch = line.match(/^(\s*)(\d+)\.\s?(.*)$/);
                            if (numberedMatch) {
                              const [, parentIndent, number, parentRest] = numberedMatch;
                              if (parentRest && parentRest.trim() !== '') {
                                // Increment number
                                const nextNumber = parseInt(number) + 1;
                                // Ensure no double newline - beforeCurrentLine already ends with newline from previous line
                                const newLine = `${parentIndent}${nextNumber}. `;
                                const newValue = beforeCurrentLine + newLine + afterCurrentLine;
                                setContent(newValue);
                                const newPos = beforeCurrentLine.length + newLine.length;
                                setTimeout(() => {
                                  textarea.setSelectionRange(newPos, newPos);
                                }, 0);
                                return;
                              }
                            }
                          }
                        }

                        // No parent found, just exit the list (remove empty sub-point line)
                        // beforeCurrentLine already ends with newline, so just append afterCurrentLine
                        const newValue = beforeCurrentLine + afterCurrentLine;
                        setContent(newValue);
                        const newPos = beforeCurrentLine.length;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      } else {
                        // Get next sub-point
                        let nextSubPoint = '';
                        const lowerSubPoint = subPoint.toLowerCase();
                        if (lowerSubPoint === 'i' || lowerSubPoint === 'a') {
                          nextSubPoint = lowerSubPoint === 'i' ? 'ii' : 'b';
                        } else if (lowerSubPoint === 'ii' || lowerSubPoint === 'b') {
                          nextSubPoint = lowerSubPoint === 'ii' ? 'iii' : 'c';
                        } else if (lowerSubPoint === 'iii' || lowerSubPoint === 'c') {
                          nextSubPoint = lowerSubPoint === 'iii' ? 'iv' : 'd';
                        } else if (lowerSubPoint === 'iv' || lowerSubPoint === 'd') {
                          nextSubPoint = lowerSubPoint === 'iv' ? 'v' : 'e';
                        } else {
                          // For other cases, try to increment
                          const isLetter = /^[a-z]$/i.test(lowerSubPoint);
                          if (isLetter) {
                            nextSubPoint = String.fromCharCode(lowerSubPoint.charCodeAt(0) + 1);
                          } else {
                            // Roman numeral - simple increment
                            const romanMap: { [key: string]: string } = { 'i': 'ii', 'ii': 'iii', 'iii': 'iv', 'iv': 'v', 'v': 'vi', 'vi': 'vii', 'vii': 'viii', 'viii': 'ix', 'ix': 'x' };
                            nextSubPoint = romanMap[lowerSubPoint] || 'ii';
                          }
                        }
                        const newLine = `\n${indent}${nextSubPoint}. `;
                        const newValue = value.substring(0, start) + newLine + afterCursor;
                        setContent(newValue);
                        const newPos = start + newLine.length;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      }
                      return;
                    }

                    // Check for regular numbered lists (1., 2., 3.) - with or without space after dot
                    const numberedListMatch = currentLine.match(/^(\s*)(\d+)\.\s?(.*)$/);
                    if (numberedListMatch) {
                      e.preventDefault();
                      const [, indent, number, rest] = numberedListMatch;

                      // If line is empty (just number and optional dot/space), exit the list and remove the empty line
                      if (!rest || rest.trim() === '') {
                        // Remove the current empty numbered line first
                        const beforeCurrentLine = value.substring(0, lineStart);
                        // Get content after cursor, removing the empty numbered line
                        let afterCurrentLine = afterCursor;

                        // Remove leading newline from afterCursor if it exists
                        if (afterCurrentLine.startsWith('\n')) {
                          afterCurrentLine = afterCurrentLine.substring(1);
                        }

                        // Exit list - remove empty line and insert newline
                        // beforeCurrentLine already ends with newline from previous line
                        const newValue = beforeCurrentLine + afterCurrentLine;
                        setContent(newValue);
                        const newPos = beforeCurrentLine.length;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      } else {
                        // Continue list - insert next number (always add space after dot for consistency)
                        const nextNumber = parseInt(number) + 1;
                        const newLine = `\n${indent}${nextNumber}. `;
                        const newValue = value.substring(0, start) + newLine + afterCursor;
                        setContent(newValue);
                        const newPos = start + newLine.length;
                        setTimeout(() => {
                          textarea.setSelectionRange(newPos, newPos);
                        }, 0);
                      }
                    }
                  }
                }}
                placeholder="Type here to add your notes..."
                className="w-full h-full p-6 resize-none border-0 focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-mono text-sm leading-relaxed"
                style={{ minHeight: '400px' }}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {!isPreviewMode && 'Tip: Select text and use toolbar buttons to format'}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Saving...' : 'Save Notes'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NoteEditor;

